import {
    FieldValueType,
    FieldWrapperImperativeHandle,
} from "components/UI/Table/UI/InteractiveCell/components/FieldWrapper.tsx";
import { isWithFormControl } from "components/UI/Table/UI/InteractiveCell/miscs/utils.ts";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { FieldValues, Path, useFormContext } from "react-hook-form";
import { FormItemProps } from "react-hook-form-antd";

const areValuesEqual = (a: FieldValueType, b: FieldValueType): boolean => {
    if (a === b) return true;
    if (dayjs.isDayjs(a) && dayjs.isDayjs(b)) return a.isSame(b);
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((v, i) => v === b[i]);
    }
    return false;
};

interface Args<T extends FieldValues, C extends FieldValueType> {
    formControl: Omit<FormItemProps<T>, "children"> | undefined;
    value: C;
}

export function useSafeFormContext<T extends FieldValues>() {
    try {
        return useFormContext<T>();
    } catch {
        return null;
    }
}

export const useInteractiveCell = <T extends FieldValues, C extends FieldValueType>({
    formControl,
    value,
}: Args<T, C>) => {
    const editableFieldRef = useRef<FieldWrapperImperativeHandle>(null);

    const isFormControlled = isWithFormControl(formControl);
    const formContext = useSafeFormContext<T>();

    const field = formContext?.watch(formControl?.name as unknown as Path<T>);
    const error = formContext?.formState?.errors?.[formControl?.name as keyof T];

    const [controlledValue, setControlledValue] = useState(value);
    const lastSavedValueRef = useRef<FieldValueType>(null);
    const hasPendingSavedValueRef = useRef(false);
    const hasSeenDifferentValueRef = useRef(false);
    const [, forceRender] = useState(0);

    // Sync controlledValue when prop value changes (e.g., after save and refetch)
    useEffect(() => {
        setControlledValue(value);
    }, [value]);

    // For form-controlled: use saved value if pending, otherwise use field
    // For non-form-controlled: use controlledValue
    const resolvedValue = (() => {
        if (isFormControlled) {
            if (hasPendingSavedValueRef.current) {
                const equal = areValuesEqual(field, lastSavedValueRef.current);

                if (!equal) {
                    // Field differs from saved - mark that we've seen stale data
                    hasSeenDifferentValueRef.current = true;
                    return lastSavedValueRef.current;
                }

                if (equal && hasSeenDifferentValueRef.current) {
                    // Field matches saved AND we've seen it differ before - server confirmed
                    hasPendingSavedValueRef.current = false;
                    hasSeenDifferentValueRef.current = false;
                    return field;
                }

                // Field matches saved but haven't seen different yet - keep showing saved
                return lastSavedValueRef.current;
            }
            return field;
        }
        return controlledValue;
    })();

    // Track when a value is saved to prevent reverting
    const markValueAsSaved = (savedValue: FieldValueType) => {
        lastSavedValueRef.current = savedValue;
        hasPendingSavedValueRef.current = true;
        hasSeenDifferentValueRef.current = false;
        forceRender((n) => n + 1);
    };

    // Function to get the latest form value (avoids stale closure issues)
    const getLatestValue = () => {
        if (isFormControlled && formContext) {
            return formContext.getValues(formControl?.name as unknown as Path<T>);
        }
        return controlledValue;
    };

    const handleOnSaveSideEffects = () => {
        if (!editableFieldRef?.current) {
            return;
        }

        // Mark current value as saved to prevent reverting to stale data
        // Use getLatestValue() to avoid stale closure issues
        markValueAsSaved(getLatestValue());

        const { triggeredByClick, setTriggeredByClick, setIsEditMode, setBlockTriggerOnHover } =
            editableFieldRef.current;

        if (triggeredByClick) {
            setTriggeredByClick(false);
        }

        setIsEditMode(false);
        setBlockTriggerOnHover(true);
    };

    const handleFocus = () => {
        if (!editableFieldRef?.current) {
            return;
        }

        const { setIsEditMode } = editableFieldRef.current;
        setIsEditMode(true);
    };

    const handleBlur = () => {
        if (!editableFieldRef?.current) {
            return;
        }

        const { setIsEditMode, elementRef, triggeredByClick } = editableFieldRef.current;
        if (!elementRef?.current?.contains(document.activeElement) && !triggeredByClick && !resolvedValue) {
            setIsEditMode(false);
        }
    };

    return {
        fieldRef: editableFieldRef,
        handleOnSaveSideEffects,
        handleBlur,
        handleFocus,
        fieldValue: field,
        resolvedValue,
        getLatestValue,
        controlledValue,
        setControlledValue,
        isFormControlled,
        error,
    };
};
